import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { newIdempotencyKey } from '@/lib/idempotency';
import { trpc } from '@/lib/trpc';
import type { SignalRow } from '@tennis-scanner/shared';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

// Modal for signals.supersedeManual — operator corrects a previously
// loaded manual pick (wrong targetOdds / wrong validUntil window / forgot
// to set a stake).
//
// V1 only allows changing targetOdds + validUntilMinutes + manualStakeUsd
// + notes. The match / market / selection are inherited from the original
// — changing those would be a different pick, not a correction.

interface Props {
  signal: SignalRow | null;
  onClose: () => void;
}

export function EditPickDialog({ signal, onClose }: Props) {
  const utils = trpc.useUtils();
  const [targetOdds, setTargetOdds] = useState('');
  const [validUntilMinutes, setValidUntilMinutes] = useState('240');
  const [stake, setStake] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!signal) return;
    setTargetOdds(signal.targetOdds.toFixed(3));
    const meta = signal.metadata ?? {};
    const m = meta.validUntilMinutes;
    setValidUntilMinutes(typeof m === 'number' ? String(m) : '240');
    const s = meta.manualStakeUsd;
    setStake(typeof s === 'number' && s > 0 ? s.toFixed(2) : '');
    const n = meta.notes;
    setNotes(typeof n === 'string' ? n : '');
  }, [signal]);

  const mutation = trpc.signals.supersedeManual.useMutation({
    onSuccess: async () => {
      toast.success('Pick corregido', {
        description: 'El anterior queda marcado como superseded.',
      });
      await Promise.all([
        utils.signals.tracked.invalidate(),
        utils.signals.list.invalidate(),
        utils.opportunities.list.invalidate(),
      ]);
      onClose();
    },
    onError: (err) => toast.error('No se pudo corregir', { description: err.message }),
  });

  const oddsNum = Number.parseFloat(targetOdds);
  const minutesNum = Number.parseInt(validUntilMinutes, 10);
  const stakeNum = stake.trim() === '' ? null : Number.parseFloat(stake);
  const canSubmit =
    !!signal &&
    Number.isFinite(oddsNum) &&
    oddsNum >= 1.01 &&
    oddsNum <= 1000 &&
    Number.isInteger(minutesNum) &&
    minutesNum >= 15 &&
    minutesNum <= 60 * 24 * 14 &&
    (stakeNum === null || (Number.isFinite(stakeNum) && stakeNum >= 0 && stakeNum <= 1_000_000));

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit || !signal) return;
    mutation.mutate({
      supersedesId: signal.id,
      targetOdds: oddsNum,
      validUntilMinutes: minutesNum,
      manualStakeUsd: stakeNum ?? undefined,
      notes: notes.trim() || undefined,
      idempotencyKey: newIdempotencyKey(),
    });
  }

  return (
    <Dialog
      open={signal !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Corregir pick manual</DialogTitle>
          <DialogDescription>
            Cambiá la cuota mínima y/o la ventana válida. El pick original queda marcado como
            superseded; el nuevo aparece en lugar suyo.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="ed-odds">Target odds (3 decimales)</Label>
              <Input
                id="ed-odds"
                type="number"
                step="0.001"
                min="1.01"
                max="1000"
                value={targetOdds}
                onChange={(e) => setTargetOdds(e.target.value)}
                autoFocus
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ed-window">Válido (minutos)</Label>
              <Input
                id="ed-window"
                type="number"
                step="1"
                min="15"
                max={60 * 24 * 14}
                value={validUntilMinutes}
                onChange={(e) => setValidUntilMinutes(e.target.value)}
              />
              <p className="text-[10px] font-mono text-muted-foreground">
                el server extiende el plazo hasta el inicio del partido + 30min si tu valor es
                menor.
              </p>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ed-stake">
              Stake USD{' '}
              <span className="text-[10px] font-mono text-muted-foreground">(opcional)</span>
            </Label>
            <Input
              id="ed-stake"
              type="number"
              step="1"
              min="0"
              max={1_000_000}
              value={stake}
              onChange={(e) => setStake(e.target.value)}
              placeholder="—"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ed-notes">Nota (opcional)</Label>
            <Input
              id="ed-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit || mutation.isPending}>
              {mutation.isPending ? 'Corrigiendo…' : 'Confirmar corrección'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
